import {
  INTRANET_SHORTCUT_ICONS,
  IntranetLinkTone,
  IntranetModuleKind,
  SITE_DIARY_DEFAULT_LABEL,
  SITE_DIARY_DELIVERY_LABELS,
  SiteDiaryDelivery,
  SiteDiaryLogoMode,
  siteDiaryDeliveryMessage,
  type IntranetModuleInstance,
} from '@gb-ticket/shared-types';
import { Loader2Icon, PencilIcon, PlusIcon } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { ConfirmDeleteDialog } from '@/products/intranet/components/confirm-delete-dialog';
import {
  intranetPanelClassName,
  intranetTableFrameClassName,
} from '@/products/intranet/features/intranet/intranet-panel';
import { useIntranetCategoryPosterRoles } from '@/products/intranet/features/intranet/use-intranet';
import {
  useCreateIntranetModule,
  useDeleteIntranetModule,
  useIntranetColleagues,
  useIntranetModuleKinds,
  useIntranetModuleReports,
  useIntranetModules,
  useResendIntranetModuleReport,
  useUpdateIntranetModule,
  useUploadIntranetModuleLogo,
} from '@/products/intranet/features/intranet/use-intranet-directory';

type ShortcutTone = (typeof IntranetLinkTone)[keyof typeof IntranetLinkTone];
type ShortcutIcon = (typeof INTRANET_SHORTCUT_ICONS)[number];

type ModuleDraft = {
  kind: typeof IntranetModuleKind.SITE_DIARY;
  label: string;
  iconName: ShortcutIcon;
  tone: ShortcutTone;
  sortOrder: string;
  enabled: boolean;
  numberPrefix: string;
  logoMode: typeof SiteDiaryLogoMode.PORTAL | typeof SiteDiaryLogoMode.CUSTOM;
  audienceRoleIds: string[];
  recipientRoleIds: string[];
  recipientUserIds: string[];
  delivery: SiteDiaryDelivery;
  logoFile: File | null;
};

const TONE_LABEL: Record<ShortcutTone, string> = {
  navy: 'Navy',
  red: 'Rot',
  green: 'Grün',
  amber: 'Bernstein',
};

const EMPTY: ModuleDraft = {
  kind: IntranetModuleKind.SITE_DIARY,
  label: SITE_DIARY_DEFAULT_LABEL,
  iconName: 'ClipboardList',
  tone: IntranetLinkTone.NAVY,
  sortOrder: '0',
  enabled: true,
  numberPrefix: 'BT',
  logoMode: SiteDiaryLogoMode.PORTAL,
  audienceRoleIds: [],
  recipientRoleIds: [],
  recipientUserIds: [],
  delivery: SiteDiaryDelivery.EMAIL,
  logoFile: null,
};

/** Editorial Module section between shortcuts and gadgets. */
export function ModuleManageSection() {
  const kinds = useIntranetModuleKinds();
  const modules = useIntranetModules();
  const roles = useIntranetCategoryPosterRoles();
  const [personQuery, setPersonQuery] = useState('');
  const colleagues = useIntranetColleagues({ q: personQuery || undefined, pageSize: 100 });
  const create = useCreateIntranetModule();
  const update = useUpdateIntranetModule();
  const remove = useDeleteIntranetModule();
  const uploadLogo = useUploadIntranetModuleLogo();
  const [draft, setDraft] = useState<ModuleDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<IntranetModuleInstance | null>(null);
  const items = modules.data?.items ?? [];
  const kindLabel = kinds.data?.items[0]?.labelDe ?? 'Bautagesbericht';
  const saving = create.isPending || update.isPending || uploadLogo.isPending || remove.isPending;

  const selectedPeople = useMemo(() => {
    const byId = new Map(
      (colleagues.data?.items ?? []).map((person) => [person.id, person.displayName] as const),
    );
    return draft.recipientUserIds.map((id) => ({
      id,
      name: byId.get(id) ?? 'Ausgewählte Person',
    }));
  }, [colleagues.data?.items, draft.recipientUserIds]);

  function startEdit(instance: IntranetModuleInstance) {
    setEditingId(instance.id);
    setDraft({
      kind: IntranetModuleKind.SITE_DIARY,
      label: instance.label,
      iconName: instance.iconName as ShortcutIcon,
      tone: instance.tone,
      sortOrder: String(instance.sortOrder),
      enabled: instance.enabled,
      numberPrefix: instance.settings.numberPrefix,
      logoMode: instance.settings.logoMode,
      audienceRoleIds: [...instance.settings.audienceRoleIds],
      recipientRoleIds: [...instance.settings.recipientRoleIds],
      recipientUserIds: [...instance.settings.recipientUserIds],
      delivery: instance.settings.delivery ?? SiteDiaryDelivery.EMAIL,
      logoFile: null,
    });
  }

  function resetForm() {
    setEditingId(null);
    setDraft(EMPTY);
    setPersonQuery('');
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (draft.logoMode === SiteDiaryLogoMode.CUSTOM && !editingId && !draft.logoFile) {
      toast.error('Bitte ein Logo für diese Instanz wählen.');
      return;
    }
    if (
      draft.enabled &&
      draft.recipientRoleIds.length === 0 &&
      draft.recipientUserIds.length === 0
    ) {
      toast.error('Bitte mindestens eine Empfängergruppe oder eine Person wählen.');
      return;
    }
    if (draft.enabled && draft.audienceRoleIds.length === 0) {
      toast.error('Bitte mindestens eine Sichtgruppe wählen.');
      return;
    }

    const numberPrefix = draft.numberPrefix.trim().toUpperCase();
    const common = {
      label: draft.label.trim(),
      iconName: draft.iconName,
      tone: draft.tone,
      sortOrder: Number(draft.sortOrder) || 0,
      enabled: draft.enabled,
    };

    try {
      if (editingId) {
        const existing = items.find((item) => item.id === editingId);
        let logoMode = draft.logoMode;
        let logoStorageKey =
          logoMode === SiteDiaryLogoMode.CUSTOM
            ? (existing?.settings.logoStorageKey ?? null)
            : null;

        if (draft.logoFile) {
          const uploaded = await uploadLogo.mutateAsync({
            id: editingId,
            file: draft.logoFile,
          });
          logoMode = SiteDiaryLogoMode.CUSTOM;
          logoStorageKey = uploaded.settings.logoStorageKey;
        }

        if (logoMode === SiteDiaryLogoMode.CUSTOM && !logoStorageKey) {
          toast.error('Bitte ein Logo für diese Instanz wählen.');
          return;
        }

        await update.mutateAsync({
          id: editingId,
          body: {
            ...common,
            settings: {
              numberPrefix,
              logoMode,
              logoStorageKey,
              audienceRoleIds: draft.audienceRoleIds,
              recipientRoleIds: draft.recipientRoleIds,
              recipientUserIds: draft.recipientUserIds,
              delivery: draft.delivery,
            },
          },
        });
        toast.success('Modul gespeichert.');
      } else {
        const created = await create.mutateAsync({
          kind: IntranetModuleKind.SITE_DIARY,
          ...common,
          settings: {
            numberPrefix,
            logoMode: SiteDiaryLogoMode.PORTAL,
            logoStorageKey: null,
            audienceRoleIds: draft.audienceRoleIds,
            recipientRoleIds: draft.recipientRoleIds,
            recipientUserIds: draft.recipientUserIds,
            delivery: draft.delivery,
          },
        });
        if (draft.logoFile) {
          await uploadLogo.mutateAsync({ id: created.id, file: draft.logoFile });
        }
        toast.success('Modul angelegt.');
      }
      resetForm();
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.');
    }
  }

  return (
    <section className="space-y-3" aria-labelledby="intranet-modules">
      <h2 id="intranet-modules" className="text-lg font-semibold">
        Module
      </h2>
      <p className="text-sm text-muted-foreground">
        Formular-Kacheln im Schnellzugriff. Dieselbe Art darf mehrfach vorkommen; jede Instanz hat
        eigene Gruppen und Empfänger.
      </p>

      <form
        className={`${intranetPanelClassName} grid gap-3 sm:grid-cols-2`}
        onSubmit={(event) => void onSubmit(event)}
      >
        <div className="space-y-1">
          <Label htmlFor="module-kind">Art</Label>
          <Select value={draft.kind} disabled>
            <SelectTrigger id="module-kind" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={IntranetModuleKind.SITE_DIARY}>{kindLabel}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-label">Bezeichnung</Label>
          <Input
            id="module-label"
            value={draft.label}
            maxLength={40}
            required
            onChange={(event) => setDraft({ ...draft, label: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-icon">Icon</Label>
          <Select
            value={draft.iconName}
            onValueChange={(value) => setDraft({ ...draft, iconName: value as ShortcutIcon })}
          >
            <SelectTrigger id="module-icon" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTRANET_SHORTCUT_ICONS.map((name) => (
                <SelectItem key={name} value={name}>
                  {name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-tone">Farbe</Label>
          <Select
            value={draft.tone}
            onValueChange={(value) => setDraft({ ...draft, tone: value as ShortcutTone })}
          >
            <SelectTrigger id="module-tone" className="h-11 w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(IntranetLinkTone).map((tone) => (
                <SelectItem key={tone} value={tone}>
                  {TONE_LABEL[tone]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-order">Reihenfolge</Label>
          <Input
            id="module-order"
            inputMode="numeric"
            value={draft.sortOrder}
            onChange={(event) => setDraft({ ...draft, sortOrder: event.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="module-prefix">Prefix der Nummer</Label>
          <Input
            id="module-prefix"
            value={draft.numberPrefix}
            maxLength={6}
            className="uppercase"
            onChange={(event) => setDraft({ ...draft, numberPrefix: event.target.value })}
          />
        </div>
        <label className="flex min-h-11 items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
          />
          Sichtbar
        </label>
        <div className="space-y-2 sm:col-span-2">
          <Label>Logo</Label>
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="radio"
                name="module-logo-mode"
                checked={draft.logoMode === SiteDiaryLogoMode.PORTAL}
                onChange={() =>
                  setDraft({ ...draft, logoMode: SiteDiaryLogoMode.PORTAL, logoFile: null })
                }
              />
              Portal-Logo
            </label>
            <label className="flex min-h-11 items-center gap-2">
              <input
                type="radio"
                name="module-logo-mode"
                checked={draft.logoMode === SiteDiaryLogoMode.CUSTOM}
                onChange={() => setDraft({ ...draft, logoMode: SiteDiaryLogoMode.CUSTOM })}
              />
              Eigenes Logo
            </label>
          </div>
          {draft.logoMode === SiteDiaryLogoMode.CUSTOM ? (
            <Input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(event) =>
                setDraft({ ...draft, logoFile: event.target.files?.[0] ?? null })
              }
            />
          ) : null}
          {editingId && draft.logoMode === SiteDiaryLogoMode.CUSTOM ? (
            <p className="text-xs text-muted-foreground">
              Ohne neue Datei bleibt das vorhandene Logo.
            </p>
          ) : null}
        </div>

        <RoleChecklist
          title="Sichtgruppen"
          hint="Nur diese Gruppen sehen die Kachel. Bei Sichtbar ist mindestens eine Gruppe nötig."
          roleIds={draft.audienceRoleIds}
          roles={roles.data?.items ?? []}
          pending={roles.isPending}
          onChange={(audienceRoleIds) => setDraft({ ...draft, audienceRoleIds })}
        />
        <RoleChecklist
          title="Empfängergruppen"
          hint="Aktive Mitglieder dieser Gruppen erhalten die PDF."
          roleIds={draft.recipientRoleIds}
          roles={roles.data?.items ?? []}
          pending={roles.isPending}
          onChange={(recipientRoleIds) => setDraft({ ...draft, recipientRoleIds })}
        />

        <div className="space-y-2 sm:col-span-2">
          <Label htmlFor="module-person-search">Empfänger (Personen)</Label>
          <Input
            id="module-person-search"
            value={personQuery}
            placeholder="Name suchen …"
            onChange={(event) => setPersonQuery(event.target.value)}
          />
          {selectedPeople.length > 0 ? (
            <ul className="flex flex-wrap gap-2 text-xs">
              {selectedPeople.map((person) => (
                <li key={person.id}>
                  <button
                    type="button"
                    className="rounded-md border px-2 py-1 hover:bg-muted"
                    onClick={() =>
                      setDraft({
                        ...draft,
                        recipientUserIds: draft.recipientUserIds.filter((id) => id !== person.id),
                      })
                    }
                  >
                    {person.name} ×
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
          <div className={`${intranetTableFrameClassName} max-h-48 space-y-2 overflow-y-auto p-2`}>
            {colleagues.isPending ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2Icon className="size-4 animate-spin" aria-hidden />
                Personen werden geladen …
              </p>
            ) : null}
            {(colleagues.data?.items ?? []).map((person) => (
              <label
                key={person.id}
                className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
              >
                <Checkbox
                  checked={draft.recipientUserIds.includes(person.id)}
                  onCheckedChange={(checked) =>
                    setDraft({
                      ...draft,
                      recipientUserIds:
                        checked === true
                          ? [...new Set([...draft.recipientUserIds, person.id])]
                          : draft.recipientUserIds.filter((id) => id !== person.id),
                    })
                  }
                />
                <span>
                  <span className="font-medium">{person.displayName}</span>
                  {person.departmentName ? (
                    <span className="block text-xs text-muted-foreground">
                      {person.departmentName}
                    </span>
                  ) : null}
                </span>
              </label>
            ))}
            {!colleagues.isPending && (colleagues.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-muted-foreground">Keine Personen gefunden.</p>
            ) : null}
          </div>
        </div>

        <fieldset className="space-y-2 sm:col-span-2">
          <legend className="text-sm font-medium">Versand</legend>
          <div className="flex flex-wrap gap-4 text-sm">
            {(
              [
                SiteDiaryDelivery.NOTIFICATION,
                SiteDiaryDelivery.EMAIL,
                SiteDiaryDelivery.BOTH,
              ] as const
            ).map((value) => (
              <label key={value} className="flex min-h-11 items-center gap-2">
                <input
                  type="radio"
                  name="module-delivery"
                  checked={draft.delivery === value}
                  onChange={() => setDraft({ ...draft, delivery: value })}
                />
                {SITE_DIARY_DELIVERY_LABELS[value]}
              </label>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            Die Person kann Glocke, E-Mail und Push unter Profil, Mitteilungen abschalten.
          </p>
        </fieldset>

        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" disabled={saving}>
            {editingId ? <PencilIcon aria-hidden /> : <PlusIcon aria-hidden />}
            {editingId ? 'Modul speichern' : 'Modul hinzufügen'}
          </Button>
          {editingId ? (
            <Button type="button" variant="outline" onClick={resetForm}>
              Abbrechen
            </Button>
          ) : null}
        </div>
      </form>

      {editingId ? <ModuleSubmittedReports instanceId={editingId} /> : null}

      {modules.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Module werden geladen …
        </p>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine Module angelegt.</p>
      ) : (
        <div className={intranetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Bezeichnung</TableHead>
                <TableHead>Art</TableHead>
                <TableHead>Sichtbar</TableHead>
                <TableHead>Sichtgruppen</TableHead>
                <TableHead>Empfänger</TableHead>
                <TableHead>Versand</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((instance) => (
                <TableRow key={instance.id}>
                  <TableCell>{instance.label}</TableCell>
                  <TableCell>{instance.kindLabel}</TableCell>
                  <TableCell>{instance.enabled ? 'Ja' : 'Nein'}</TableCell>
                  <TableCell>{instance.settings.audienceRoleIds.length}</TableCell>
                  <TableCell>{instance.recipientCount}</TableCell>
                  <TableCell>
                    {
                      SITE_DIARY_DELIVERY_LABELS[
                        instance.settings.delivery ?? SiteDiaryDelivery.EMAIL
                      ]
                    }
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => startEdit(instance)}
                    >
                      Bearbeiten
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setPendingDelete(instance)}
                    >
                      Löschen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <ConfirmDeleteDialog
        open={pendingDelete !== null}
        title="Modul löschen?"
        description={
          pendingDelete
            ? `„${pendingDelete.label}“ und alle zugehörigen Berichte (Entwürfe und abgeschickte inkl. PDFs und Fotos) werden unwiderruflich gelöscht.`
            : ''
        }
        pending={remove.isPending}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          if (!pendingDelete) {
            return;
          }
          void remove
            .mutateAsync(pendingDelete.id)
            .then(() => {
              toast.success('Modul gelöscht.');
              setPendingDelete(null);
              if (editingId === pendingDelete.id) {
                resetForm();
              }
            })
            .catch((error: unknown) => {
              toast.error(
                error instanceof ApiRequestError ? error.message : 'Löschen fehlgeschlagen.',
              );
            });
        }}
      />
    </section>
  );
}

function ModuleSubmittedReports({ instanceId }: { instanceId: string }) {
  const reports = useIntranetModuleReports(instanceId);
  const resend = useResendIntranetModuleReport(instanceId);
  const submitted = (reports.data?.items ?? []).filter((entry) => entry.status === 'submitted');

  async function onResend(reportId: string) {
    try {
      const result = await resend.mutateAsync(reportId);
      toast.success(
        siteDiaryDeliveryMessage({
          mode: 'resend',
          runningNumber: null,
          delivery: result.delivery,
          mailSent: result.mailSent,
          mailError: result.mailError,
          notified: result.notified,
          notifyError: result.notifyError,
        }),
      );
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Erneut senden fehlgeschlagen.',
      );
    }
  }

  async function onDownload(reportId: string) {
    try {
      const response = await fetch(`/api/intranet/modules/${instanceId}/reports/${reportId}/pdf`, {
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('PDF konnte nicht geladen werden.');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = 'Bautagesbericht.pdf';
      anchor.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'PDF herunterladen fehlgeschlagen.');
    }
  }

  return (
    <div className={`${intranetPanelClassName} space-y-3`}>
      <h3 className="text-sm font-medium">Abgeschickte Berichte</h3>
      {reports.isPending ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Berichte werden geladen …
        </p>
      ) : submitted.length === 0 ? (
        <p className="text-sm text-muted-foreground">Noch keine abgeschickten Berichte.</p>
      ) : (
        <div className={intranetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nummer</TableHead>
                <TableHead>Datum</TableHead>
                <TableHead>Bauprojekt</TableHead>
                <TableHead>Person</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {submitted.map((report) => {
                const payload =
                  report.payload && typeof report.payload === 'object'
                    ? (report.payload as { date?: string; project?: string })
                    : {};
                return (
                  <TableRow key={report.id}>
                    <TableCell>{report.runningNumber ?? '—'}</TableCell>
                    <TableCell>{payload.date ?? '—'}</TableCell>
                    <TableCell>{payload.project ?? '—'}</TableCell>
                    <TableCell>{report.authorDisplayName ?? '—'}</TableCell>
                    <TableCell className="space-x-1 text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!report.pdfStorageKey}
                        onClick={() => void onDownload(report.id)}
                      >
                        PDF
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={resend.isPending || !report.pdfStorageKey}
                        onClick={() => void onResend(report.id)}
                      >
                        Erneut senden
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function RoleChecklist({
  title,
  hint,
  roleIds,
  roles,
  pending,
  onChange,
}: {
  title: string;
  hint: string;
  roleIds: readonly string[];
  roles: readonly { id: string; name: string; isSystem: boolean }[];
  pending: boolean;
  onChange: (roleIds: string[]) => void;
}) {
  return (
    <div className="space-y-2 sm:col-span-2">
      <Label>{title}</Label>
      <p className="text-xs text-muted-foreground">{hint}</p>
      <div className={`${intranetTableFrameClassName} max-h-48 space-y-2 overflow-y-auto p-2`}>
        {pending ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden />
            Gruppen werden geladen …
          </p>
        ) : null}
        {roles.map((role) => (
          <label
            key={role.id}
            className="flex items-start gap-3 rounded-xl border border-border bg-card px-3 py-2 text-sm shadow-sm"
          >
            <Checkbox
              checked={roleIds.includes(role.id)}
              onCheckedChange={(checked) =>
                onChange(
                  checked === true
                    ? [...new Set([...roleIds, role.id])]
                    : roleIds.filter((id) => id !== role.id),
                )
              }
            />
            <span>
              <span className="font-medium">{role.name}</span>
              <span className="block text-xs text-muted-foreground">
                {role.isSystem ? 'Systemrolle' : 'Eigene Gruppe'}
              </span>
            </span>
          </label>
        ))}
        {!pending && roles.length === 0 ? (
          <p className="text-sm text-muted-foreground">Keine Berechtigungsgruppen verfügbar.</p>
        ) : null}
      </div>
    </div>
  );
}
